import { useState } from "react";
import { Loader2, UserPlus, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

import { crmService } from "@/services/crmService";
import { ContactForm, emptyContact, contactPayload } from "@/components/crm/ContactForm";
import { parseFieldErrors } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export const AddContactDialog = ({ open, onOpenChange, onCreated, onViewExisting }) => {
  const [form, setForm] = useState(emptyContact);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [duplicate, setDuplicate] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const reset = () => { setForm(emptyContact); setErrors({}); setDuplicate(null); };

  const handleOpenChange = (v) => { if (!v) reset(); onOpenChange(v); };

  const save = async (force = false) => {
    setErrors({});
    setSaving(true);
    try {
      const res = await crmService.createContact(contactPayload(form), force);
      if (res.created === false && res.duplicate) {
        setDuplicate(res.duplicate);
        return;
      }
      toast.success("Contact added");
      reset();
      onCreated?.(res.contact);
    } catch (err) {
      setErrors(parseFieldErrors(err));
      toast.error(err.message || "Please correct the highlighted fields");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="add-contact-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UserPlus className="h-5 w-5 text-accent" /> Add Contact</DialogTitle>
          <DialogDescription>Create a new master contact record in your CRM.</DialogDescription>
        </DialogHeader>

        {duplicate ? (
          <div className="space-y-4" data-testid="duplicate-warning">
            <div className="flex items-start gap-3 rounded-lg border border-amber-400/50 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
              <div>
                <p className="font-semibold">Possible duplicate found</p>
                <p className="mt-1">A contact with the same email or phone already exists in your workspace:</p>
                <p className="mt-2 font-medium">
                  {duplicate.first_name} {duplicate.last_name}
                  {duplicate.email ? ` · ${duplicate.email}` : ""}{duplicate.phone ? ` · ${duplicate.phone}` : ""}
                </p>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => { onViewExisting?.(duplicate.id); handleOpenChange(false); }} data-testid="dup-view-existing">
                View Existing
              </Button>
              <Button variant="secondary" onClick={() => setDuplicate(null)} data-testid="dup-back">Back to edit</Button>
              <Button onClick={() => save(true)} disabled={saving} data-testid="dup-create-anyway">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Anyway"}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <>
            <ContactForm form={form} set={set} errors={errors} idPrefix="add" />
            <DialogFooter>
              <Button variant="outline" onClick={() => handleOpenChange(false)} data-testid="add-contact-cancel">Cancel</Button>
              <Button onClick={() => save(false)} disabled={saving} data-testid="add-contact-save">
                {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : "Save Contact"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
